import { useMemo } from "react";
import {
  type AnnouncementRequest,
  type DesktopLayoutEvent,
  transitionDesktopLayout,
} from "@/app/layouts/desktopLayoutMediator";
import type { DesktopAnnouncementModalController } from "@/features/announce/hooks/useDesktopAnnouncementModal";
import { useSettingsStore } from "@/features/settings/hooks";
import { resolveDensityAttribute } from "@/features/settings/utils/renderSettings";
import { useIsPcForced } from "@/shared/hooks/useMediaQuery";
import {
  createMediator,
  type EventNode,
  type Mediator,
  type TypedEvent,
} from "@/shared/lib/componentHierarchy";
import {
  getHierarchyResourceSnapshot,
  useComponentNode,
  useHierarchyResource,
  useMediatorSnapshot,
} from "@/shared/ui/component-hierarchy/hooks";

export interface DesktopLayoutPresentation {
  node: EventNode<TypedEvent>;
  isPcForced: boolean;
  density: "compact" | undefined;
  headerFixed: boolean;
  replyButtonFixed: boolean;
  announcementModalRequest: AnnouncementRequest | null;
  announcementModalController: DesktopAnnouncementModalController;
}

export function useDesktopLayoutPresenter(): DesktopLayoutPresentation {
  const isPcForced = useIsPcForced();
  const density = resolveDensityAttribute(
    useSettingsStore((state) => state.displayDensity),
  );
  const headerFixed = useSettingsStore((state) => state.headerFixed);
  const replyButtonFixed = useSettingsStore((state) => state.replyButtonFixed);
  const mediator = useHierarchyResource<
    Mediator<AnnouncementRequest | null, TypedEvent>
  >(
    (previous) =>
      createMediator(
        previous?.isDisposed
          ? (getHierarchyResourceSnapshot<AnnouncementRequest | null>(
              previous,
            ) ?? null)
          : null,
        transitionDesktopLayout,
      ),
    [],
  );
  const announcementModalRequest = useMediatorSnapshot(mediator);
  const node = useComponentNode((event) =>
    mediator.dispatch(event).status === "handled" ? "handled" : "unhandled",
  );
  const announcementModalController =
    useMemo<DesktopAnnouncementModalController>(
      () => ({
        openAnnouncementModal(announcementId?: number): void {
          const event: DesktopLayoutEvent = {
            type: "desktop-layout.announcement.open",
            announcementId,
          };
          node.dispatch(event);
        },
      }),
      [node],
    );
  return {
    node,
    isPcForced,
    density,
    headerFixed,
    replyButtonFixed,
    announcementModalRequest,
    announcementModalController,
  };
}
